import { useState, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { BackButton } from '@/components/ui/back-button';
import { DomainSelectionStep } from '@/components/dfy/DomainSelectionStep';
import { AccountSetupStep, MAX_ACCOUNTS_PER_DOMAIN } from '@/components/dfy/AccountSetupStep';
import { AccountTable } from '@/components/dfy/AccountTable';
import { OrderSummary } from '@/components/dfy/OrderSummary';
import { useUser } from '@/lib/hooks/useUser';
import { generateUsernameVariations } from '@/components/dfy/utils';
import { dfyService } from '@/lib/services/dfy';
import { toast } from 'sonner';
import type { DomainSearchResult, SelectedDomain, EmailAccount } from '@/components/dfy/types';

function detectCurrency(): 'gbp' | 'usd' {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return tz.startsWith('Europe/London') || tz.startsWith('Europe/Belfast') ? 'gbp' : 'usd';
  } catch {
    return 'usd';
  }
}

const STORAGE_KEY = 'dfy-setup-progress';

interface SavedProgress {
  step: 1 | 2;
  selectedDomains: SelectedDomain[];
  accounts: EmailAccount[];
  selectedTlds: string[];
  lastQuery: string;
}

function loadProgress(): Partial<SavedProgress> {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    return JSON.parse(raw);
  } catch {
    return {};
  }
}

function saveProgress(data: SavedProgress) {
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
}

export default function DfySetup() {
  const { user } = useUser();
  const [searchParams, setSearchParams] = useSearchParams();
  const [saved] = useState(loadProgress);
  const [step, setStep] = useState<1 | 2>(saved.step ?? 1);
  const [searchResults, setSearchResults] = useState<DomainSearchResult[]>([]);
  const [selectedDomains, setSelectedDomains] = useState<SelectedDomain[]>(saved.selectedDomains ?? []);
  const [accounts, setAccounts] = useState<EmailAccount[]>(saved.accounts ?? []);
  const [isSearching, setIsSearching] = useState(false);
  const [apiUnavailable, setApiUnavailable] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedTlds, setSelectedTlds] = useState<string[]>(saved.selectedTlds ?? ['com', 'org']);
  const [lastQuery, setLastQuery] = useState<string>(saved.lastQuery ?? '');
  const [currency] = useState<'usd' | 'gbp'>(detectCurrency);
  const [domainPriceCents, setDomainPriceCents] = useState(currency === 'gbp' ? 1000 : 1500);
  const [accountPriceCents, setAccountPriceCents] = useState(currency === 'gbp' ? 500 : 700);
  const [currencySymbol, setCurrencySymbol] = useState(currency === 'gbp' ? '£' : '$');

  // Fetch pricing from server
  useEffect(() => {
    dfyService.getPricing(currency).then((p) => {
      setDomainPriceCents(p.domainPriceCents);
      setAccountPriceCents(p.accountPriceCents);
      setCurrencySymbol(p.symbol);
    }).catch(() => {});
  }, [currency]);

  // Save progress for page refreshes, clear when navigating away
  useEffect(() => {
    saveProgress({ step, selectedDomains, accounts, selectedTlds, lastQuery });
  }, [step, selectedDomains, accounts, selectedTlds, lastQuery]);

  useEffect(() => {
    return () => {
      sessionStorage.removeItem(STORAGE_KEY);
    };
  }, []);

  const handleSearch = useCallback(async (query: string, tlds: string[] = selectedTlds, preserveSelections = false) => {
    setIsSearching(true);
    setApiUnavailable(false);
    if (!preserveSelections) setSelectedDomains([]);
    setLastQuery(query);
    try {
      const response = await dfyService.searchDomains(query, tlds, currency);
      setSearchResults(response.domains);
      setApiUnavailable(response.apiUnavailable);
    } catch (error) {
      console.error('Domain search error:', error);
      toast.error('Failed to search domains');
    } finally {
      setIsSearching(false);
    }
  }, [selectedTlds, currency]);

  // Re-run search on mount if we had a saved query (to repopulate searchResults)
  useEffect(() => {
    if (saved.lastQuery && saved.selectedTlds?.length) {
      handleSearch(saved.lastQuery, saved.selectedTlds, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cancel order if user backed out of Stripe checkout
  useEffect(() => {
    const cancelledOrderId = searchParams.get('cancelled_order');
    if (cancelledOrderId) {
      dfyService.cancelOrder(cancelledOrderId).catch(() => {});
      searchParams.delete('cancelled_order');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const handleToggleTld = useCallback((tld: string) => {
    setSelectedTlds(prev => {
      const newTlds = prev.includes(tld)
        ? prev.filter(t => t !== tld)
        : [...prev, tld];

      // Re-run search with new TLDs if we have a query
      if (lastQuery && newTlds.length > 0) {
        handleSearch(lastQuery, newTlds);
      }

      return newTlds;
    });
  }, [lastQuery, handleSearch]);

  const handleToggleDomain = useCallback((domain: DomainSearchResult) => {
    if (!domain.available || domain.price === null) return;

    setSelectedDomains(prev => {
      const exists = prev.some(d => d.domain === domain.domain);
      if (exists) {
        return prev.filter(d => d.domain !== domain.domain);
      }
      return [...prev, { domain: domain.domain, price: domain.price! }];
    });
  }, []);

  const handleUnselectAll = useCallback(() => {
    setSelectedDomains([]);
  }, []);

  const handleNext = useCallback(() => {
    if (step === 1 && selectedDomains.length > 0) {
      // Always regenerate accounts based on currently selected domains
      if (user?.name) {
        const [firstName = '', lastName = ''] = user.name.split(' ');
        const usernames = generateUsernameVariations(firstName, lastName);
        const newAccounts: EmailAccount[] = [];

        for (const domain of selectedDomains) {
          for (const username of usernames) {
            if (newAccounts.filter(a => a.domain === domain.domain).length >= MAX_ACCOUNTS_PER_DOMAIN) {
              break;
            }
            newAccounts.push({
              id: `${username}-${domain.domain}`,
              username,
              domain: domain.domain,
              email: `${username}@${domain.domain}`,
              personaFirstName: firstName,
              personaLastName: lastName,
            });
          }
        }
        setAccounts(newAccounts);
      }
      setStep(2);
    }
  }, [step, selectedDomains, user?.name]);

  const handleBack = useCallback(() => {
    if (step === 2) {
      setAccounts([]);
      setStep(1);
    }
  }, [step]);

  const handleAddSender = useCallback((username: string, domain: string, firstName: string, lastName: string) => {
    const newAccounts: EmailAccount[] = [];

    // If "all" is selected, add to all domains
    const domainsToAdd = domain === 'all'
      ? selectedDomains.map(d => d.domain)
      : [domain];

    for (const d of domainsToAdd) {
      const domainAccounts = accounts.filter(a => a.domain === d);
      if (domainAccounts.length >= MAX_ACCOUNTS_PER_DOMAIN) continue;

      const accountId = `${username}-${d}`;
      if (accounts.some(a => a.id === accountId)) continue;

      newAccounts.push({
        id: accountId,
        username,
        domain: d,
        email: `${username}@${d}`,
        personaFirstName: firstName,
        personaLastName: lastName,
      });
    }

    setAccounts(prev => [...prev, ...newAccounts]);
  }, [accounts, selectedDomains]);

  const handleRemoveAccount = useCallback((accountId: string) => {
    setAccounts(prev => prev.filter(a => a.id !== accountId));
  }, []);

  const handleCompletePurchase = useCallback(async () => {
    setIsSubmitting(true);
    try {
      const response = await dfyService.createOrder({
        currency,
        domains: selectedDomains.map(d => ({ domain: d.domain, price: d.price })),
        accounts: accounts.map(a => ({
          username: a.username,
          domain: a.domain,
          personaFirstName: a.personaFirstName,
          personaLastName: a.personaLastName,
        })),
      });

      // Redirect to Stripe checkout
      if (response.checkoutUrl) {
        sessionStorage.removeItem(STORAGE_KEY);
        window.location.href = response.checkoutUrl;
        return; // Don't reset isSubmitting - user is redirecting
      }

      // Fallback if no checkout URL (shouldn't happen)
      toast.error('No checkout URL returned');
      setIsSubmitting(false);
    } catch (error) {
      console.error('Order creation error:', error);
      const message = error instanceof Error ? error.message : 'Failed to create order';
      toast.error(message);
      setIsSubmitting(false);
    }
  }, [selectedDomains, accounts]);

  return (
    <>
      <div className="px-4 mt-7">
        <div className="flex items-center gap-4">
          <BackButton
            href="/dashboard/email-accounts/add"
            tooltip="Back to Add Email Account"
          />
          <div className="flex items-center gap-3">
            <h1 className="text-xl font-semibold">Automatic Email Setup</h1>
            <span className="text-xl text-foreground">·</span>
            <span className="text-xl text-foreground">
              {step === 1 ? 'Select Domains' : 'Configure Accounts'}
            </span>
            <span className="text-sm text-foreground bg-surface-track px-2.5 py-0.5 rounded-full tracking-wider">
              {step}/2
            </span>
          </div>
        </div>
      </div>

      <div className="w-full px-8 flex justify-center mt-page-top pb-8">
        <div className="w-full max-w-6xl">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-8 items-start">
            {/* Left side - Main content */}
            <div>
              {step === 1 && (
                <DomainSelectionStep
                  variant="page"
                  searchResults={searchResults}
                  selectedDomains={selectedDomains}
                  isSearching={isSearching}
                  apiUnavailable={apiUnavailable}
                  selectedTlds={selectedTlds}
                  onSearch={handleSearch}
                  onToggleDomain={handleToggleDomain}
                  onToggleTld={handleToggleTld}
                  onUnselectAll={handleUnselectAll}
                  currencySymbol={currencySymbol}
                />
              )}

              {step === 2 && (
                <div className="space-y-3">
                  <AccountSetupStep
                    variant="page"
                    selectedDomains={selectedDomains}
                    accounts={accounts}
                    onAddSender={handleAddSender}
                    onRemoveAccount={handleRemoveAccount}
                  />
                  <AccountTable
                    accounts={accounts}
                    onRemoveAccount={handleRemoveAccount}
                    accountPrice={accountPriceCents}
                    currencySymbol={currencySymbol}
                  />
                </div>
              )}
            </div>

            {/* Right side - Summary and actions */}
            <div className="lg:sticky lg:top-8 space-y-4">
              <OrderSummary
                domainCount={selectedDomains.length}
                accountCount={step === 2 ? accounts.length : 0}
                domainPrice={domainPriceCents}
                accountPrice={accountPriceCents}
                currencySymbol={currencySymbol}
                hideAccounts={step === 1}
              />

              <div className="flex flex-col gap-2">
                {step === 1 && (
                  <Button
                    variant="primary"
                    onClick={handleNext}
                    disabled={selectedDomains.length === 0}
                    className="w-full"
                  >
                    Next
                  </Button>
                )}

                {step === 2 && (
                  <>
                    <Button
                      variant="primary"
                      onClick={handleCompletePurchase}
                      disabled={accounts.length === 0 || isSubmitting}
                      className="w-full"
                    >
                      {isSubmitting ? 'Creating Order...' : 'Complete Purchase'}
                    </Button>
                    <Button variant="secondary" onClick={handleBack} className="w-full">
                      Back
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
